import React, {
  forwardRef,
  useContext,
  KeyboardEventHandler,
  ComponentPropsWithRef,
} from "react";
import PropTypes from "prop-types";

import LpgExpandOutline from "@/icons/Lpg/ExpandOutline";

import { ReactComponentStaticProps } from "../../types/react";

import { Icon } from "../Icon";
import { AccordionHeadline } from "./AccordionHeadline";
import { AccordionSubline } from "./AccordionSubline";
import { AccordionItemContext } from "./AccordionItemContext";
import * as focusManager from "./focusManager";

import styles from "./Accordion.scss";

export type AccordionItemToggleProps = React.ComponentPropsWithRef<"div">;

const AccordionToggleWithRef = (
  props: AccordionItemToggleProps,
  ref: ComponentPropsWithRef<"div">["ref"]
) => {
  const { children, ...restProps } = props;
  const item = useContext(AccordionItemContext) || {};

  const toggleHandler: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      item.toggle();
    }

    switch (event.key) {
      case "Home": {
        event.preventDefault();
        focusManager.focusFirstSiblingOf(event.target);
        break;
      }
      case "End": {
        event.preventDefault();
        focusManager.focusLastSiblingOf(event.target);
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        event.preventDefault();
        focusManager.focusPreviousSiblingOf(event.target);
        break;
      }
      case "ArrowDown":
      case "ArrowRight": {
        event.preventDefault();
        focusManager.focusNextSiblingOf(event.target);
        break;
      }
      default: {
        //
      }
    }
  };

  return (
    <div
      ref={ref}
      className={styles.button}
      id={`accordion__heading-${item.id}`}
      aria-expanded={item.isExpanded}
      aria-controls={`accordion__panel-${item.id}`}
      role="button"
      tabIndex={0}
      data-accordion-component="AccordionItemButton"
      onClick={item.toggle}
      onKeyDown={toggleHandler.bind(item)}
      {...restProps}
    >
      <span className={styles.icon}>
        <Icon icon={LpgExpandOutline} size={Icon.Size.small} />
      </span>
      {children}
    </div>
  );
};

export const AccordionToggle = (forwardRef(
  AccordionToggleWithRef
) as unknown) as ((props: AccordionItemToggleProps) => JSX.Element) & {
  Headline: typeof AccordionHeadline;
  Subline: typeof AccordionSubline;
} & ReactComponentStaticProps<AccordionItemToggleProps>;

AccordionToggle.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

AccordionToggle.Headline = AccordionHeadline;
AccordionToggle.Subline = AccordionSubline;

AccordionToggle.displayName = "Accordion.Item.Toggle";

export default AccordionToggle;
